/**
 * Zimply Natural — Apotheken-Selector (Apothekenzuordnung)
 * --------------------------------------------------------------------------
 * Custom Element <apotheke-selector> für den Warenkorb (Drawer + Cart-Page).
 *
 *  - Liest die Apothekenliste aus der Data-Island (shop.metafields.apotheken.liste),
 *    filtert auf `aktiv === true`, zeigt nur den `name`.
 *  - Schreibt die Auswahl als Cart-Attribut `apotheke_id` (→ note_attributes).
 *  - NEM-Regel: liegt ein Nahrungsergänzungsmittel im Warenkorb, wird auf die
 *    Balde Apotheke (Default-ID 34) gesperrt; vorherige Auswahl wird gesichert
 *    und beim Entfernen des NEM wiederhergestellt.
 *  - Express-Checkout (Shop Pay/Apple Pay/PayPal) wird NICHT hier behandelt —
 *    das ist komplett backend-gesichert (n8n/Ninox).
 *
 * Muster bewusst am Theme-eigenen Gift-Wrapping orientiert
 * (theme.utils.fetchConfig / theme.pubsub / Section-Rendering-API).
 */
(function () {
  'use strict';

  var PREV_KEY = 'zimply_apotheke_prev';

  function fetchConfig() {
    if (window.theme && theme.utils && typeof theme.utils.fetchConfig === 'function') {
      return theme.utils.fetchConfig();
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    };
  }

  function cartUpdateUrl() {
    return (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js';
  }

  function debounce(fn, wait) {
    if (window.theme && theme.utils && typeof theme.utils.debounce === 'function') {
      return theme.utils.debounce(fn, wait);
    }
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Gemerkte Vor-NEM-Auswahl erst NACH dem aktuellen Tick löschen, damit bei
  // gleichzeitig aktiven Instanzen (Drawer + Cart-Page) alle denselben Wert
  // wiederherstellen, bevor der Stash verfällt.
  var prevClearTimer = null;
  function scheduleClearPrev() {
    if (prevClearTimer) return;
    prevClearTimer = setTimeout(function () {
      try { sessionStorage.removeItem(PREV_KEY); } catch (e) {}
      prevClearTimer = null;
    }, 0);
  }

  if (customElements.get('apotheke-selector')) return;

  customElements.define(
    'apotheke-selector',
    class ApothekeSelector extends HTMLElement {
      constructor() {
        super();
        this._onCartUpdate = this._onCartUpdate.bind(this);
      }

      get select() { return this.querySelector('.zn-apo__select'); }
      get labelEl() { return this.querySelector('.zn-apo__label'); }
      get hintEl() { return this.querySelector('.zn-apo__hint'); }

      get nemId() { return (this.getAttribute('data-nem-id') || '34').trim(); }
      get nemType() { return this.getAttribute('data-nem-type') || 'Nahrungsergänzungsmittel'; }
      get selectedId() { return (this.getAttribute('data-selected') || '').trim(); }
      get hasNem() { return this.getAttribute('data-has-nem') === 'true'; }
      get emptyHint() { return this.getAttribute('data-empty-hint') || ''; }
      get nemHint() { return this.getAttribute('data-nem-hint') || ''; }

      get isDesignMode() {
        return !!(window.Shopify && Shopify.designMode);
      }

      get list() {
        var node = this.querySelector('script[type="application/json"][data-apotheken-liste]');
        if (!node) return [];
        try {
          var data = JSON.parse(node.textContent);
          return Array.isArray(data) ? data : [];
        } catch (e) {
          return [];
        }
      }

      get activeList() {
        return this.list.filter(function (a) {
          return a && a.aktiv === true && a.id != null && a.name;
        });
      }

      connectedCallback() {
        if (!this.select) return;

        this.buildOptions();
        this.applyState();
        this.refreshHint();

        this.select.addEventListener('change', debounce(this.onChange.bind(this), 250));

        if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
          this._unsubscribe = theme.pubsub.subscribe(
            theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this._onCartUpdate
          );
        }
      }

      disconnectedCallback() {
        if (this._unsubscribe) this._unsubscribe();
      }

      /* ---------- Rendering ---------- */

      buildOptions() {
        var active = this.activeList;

        // Keine (aktive) Liste → Dropdown verstecken, Hinweis „wird automatisch zugewiesen".
        if (!active.length) {
          this.select.hidden = true;
          if (this.labelEl) this.labelEl.hidden = true;
          return;
        }

        this.select.hidden = false;
        if (this.labelEl) this.labelEl.hidden = false;

        var current = this.selectedId;
        var html = '<option value="">' + escapeHtml(this.emptyHint) + '</option>';
        active.forEach(function (apo) {
          var id = String(apo.id);
          var sel = id === current ? ' selected' : '';
          html += '<option value="' + escapeHtml(id) + '"' + sel + '>' + escapeHtml(apo.name) + '</option>';
        });
        this.select.innerHTML = html;

        // Re-Validierung: gewählte Apotheke ist nicht (mehr) aktiv → Attribut leeren.
        if (current && !active.some(function (a) { return String(a.id) === current; })) {
          this.select.value = '';
          this.setAttribute('data-selected', '');
          if (!this.isDesignMode) this.writeAttribute('');
        }
      }

      /* ---------- NEM-Lock / Restore ---------- */

      applyState() {
        var nemId = this.nemId;

        if (this.hasNem) {
          // Vorherige (Nicht-NEM-)Auswahl genau einmal sichern.
          if (!this.hasStashedPrev() && this.selectedId !== nemId) {
            this.stashPrev(this.selectedId);
          }
          this.lockForNem(nemId);
        } else {
          // War zuvor wegen NEM gesperrt → vorherige Auswahl wiederherstellen.
          if (this.hasStashedPrev()) {
            var prev = this.peekPrev();
            scheduleClearPrev();
            this.restoreSelection(prev);
          }
          if (this.select) this.select.disabled = false;
        }
      }

      lockForNem(nemId) {
        if (this.select && !this.select.hidden) {
          if (!this.optionExists(nemId)) {
            var apo = this.list.find(function (a) { return String(a.id) === nemId; });
            var opt = document.createElement('option');
            opt.value = nemId;
            opt.textContent = apo ? apo.name : nemId;
            this.select.appendChild(opt);
          }
          this.select.value = nemId;
          this.select.disabled = true;
        }

        // Cart-Attribut auf NEM-Apotheke setzen (Doppel-Absicherung), nur wenn abweichend.
        if (this.selectedId !== nemId && !this.isDesignMode) {
          this.setAttribute('data-selected', nemId);
          this.writeAttribute(nemId);
          this.syncSiblings(nemId);
        }
      }

      restoreSelection(prev) {
        prev = prev || '';
        if (this.select && this.optionExists(prev)) {
          this.select.value = prev;
        } else {
          if (this.select) this.select.value = '';
          prev = '';
        }
        if (this.selectedId !== prev && !this.isDesignMode) {
          this.setAttribute('data-selected', prev);
          this.writeAttribute(prev);
          this.syncSiblings(prev);
        }
      }

      /* ---------- User-Interaktion ---------- */

      onChange() {
        if (!this.select || this.select.disabled) return;
        var value = this.select.value || '';
        this.setAttribute('data-selected', value);
        if (!this.isDesignMode) this.writeAttribute(value);
        this.syncSiblings(value);
      }

      /* ---------- Cart-Events (Backup, falls eine Instanz nicht re-gerendert wird) ---------- */

      _onCartUpdate(event) {
        var cart = event && event.cart;
        if (!cart || !Array.isArray(cart.items)) return;

        var nemType = this.nemType;
        var hasNem = cart.items.some(function (it) {
          return it && it.product_type === nemType;
        });
        this.setAttribute('data-has-nem', hasNem ? 'true' : 'false');

        if (cart.attributes && Object.prototype.hasOwnProperty.call(cart.attributes, 'apotheke_id')) {
          this.setAttribute('data-selected', cart.attributes.apotheke_id || '');
        }
        this.applyState();
        this.refreshHint();
      }

      /* ---------- Helpers ---------- */

      optionExists(value) {
        if (!this.select) return false;
        return Array.prototype.some.call(this.select.options, function (o) {
          return o.value === String(value);
        });
      }

      syncSiblings(value) {
        document.querySelectorAll('apotheke-selector').forEach(function (el) {
          if (el === this) return;
          el.setAttribute('data-selected', value);
          var sel = el.querySelector('.zn-apo__select');
          if (!sel || sel.disabled) return;
          var has = Array.prototype.some.call(sel.options, function (o) { return o.value === value; });
          if (has) sel.value = value;
        }, this);
      }

      writeAttribute(value) {
        var body = JSON.stringify({ attributes: { apotheke_id: value } });
        fetch(cartUpdateUrl(), Object.assign({}, fetchConfig(), { body: body })).catch(function () {
          // Stillschweigend — die finale Zuordnung ist ohnehin backend-gesichert.
        });
      }

      showHint(text) {
        if (!this.hintEl) return;
        if (!text) { this.hideHint(); return; }
        this.hintEl.textContent = text;
        this.hintEl.hidden = false;
      }

      hideHint() {
        if (!this.hintEl) return;
        this.hintEl.textContent = '';
        this.hintEl.hidden = true;
      }

      // Eine Quelle der Wahrheit für den Hinweistext: NEM > leere Liste > kein Hinweis.
      refreshHint() {
        if (this.hasNem) { this.showHint(this.nemHint); return; }
        if (!this.activeList.length) { this.showHint(this.emptyHint); return; }
        this.hideHint();
      }

      hasStashedPrev() {
        try { return sessionStorage.getItem(PREV_KEY) !== null; } catch (e) { return false; }
      }

      stashPrev(value) {
        try { sessionStorage.setItem(PREV_KEY, value || ''); } catch (e) {}
      }

      peekPrev() {
        try { return sessionStorage.getItem(PREV_KEY) || ''; } catch (e) { return ''; }
      }
    }
  );
})();
